import { Base64 } from 'js-base64';
import { UiMode } from 'new/constants/uiMode';
import {
  getTableNames,
  mapTablesToSelection,
} from 'new/components/flows/types/cdc/CreateCDCFlow/ResourceSetupPage/getTablesFromResource';

type ColumnSelections = {
  [tableName: string]: {
    timestampColumn?: string;
    autoIncrementColumn?: string;
  };
};

type SyncModes = {
  [tableName: string]: string;
};

export const mapToSource = (config, allAvailableTables = []) => {
  const source: any = {
    name: config.name,
    description: config.description,
    flow_type: 'cdc',
    source_config: {
      'start.cron': config.frequency,
      'cdc.enabled': config.isCDCMode,
      db_query_mode: 'Default',
      'cdc.snapshot.enabled': config.snapshotEnabled,
      'cdc.mode': config.isCDCMode ? 'multi_table' : 'database_read',
      mapping: {
        mode: config.schemaMode === 'auto' ? 'auto' : 'manual',
      },
      'cdc.capture.delete': config.captureDelete,
      ...(config['ui.mode'] ? { 'ui.mode': config['ui.mode'] } : {}),
    },
  };
  const selectedTables = getTableNames(config.selection) || [];
  const isAutoMode = config.schemaMode === 'auto';

  // Add column selections, table filters, and sync modes configuration to elt.mode.config
  if (config.columnSelections || config.tableFilters || config.syncModes) {
    if (!source.source_config['elt.mode.config']) {
      source.source_config['elt.mode.config'] = {};
    }

    // Process sync modes first (takes precedence)
    if (config.syncModes) {
      Object.entries(config.syncModes as SyncModes).forEach(([tableName, syncMode]) => {
        if (syncMode === 'full_scan') {
          source.source_config['elt.mode.config'][tableName] = {
            mode: 'none',
          };
        }
      });
    }

    // Process column selections (only if not full table scan)
    if (config.columnSelections) {
      Object.entries(config.columnSelections as ColumnSelections).forEach(
        ([tableName, selections]) => {
          // Skip if this table is set to full table scan
          if (config.syncModes?.[tableName] === 'full_scan') {
            return;
          }

          if (selections && (selections.timestampColumn || selections.autoIncrementColumn)) {
            const tableConfig: any = {};

            // Set mode based on which column is selected
            if (selections.autoIncrementColumn) {
              tableConfig.mode = 'incrementing';
              tableConfig['incrementing.column.name'] = selections.autoIncrementColumn;
            } else if (selections.timestampColumn) {
              tableConfig.mode = 'timestamp';
              tableConfig['timestamp.column.name'] = selections.timestampColumn;
            }

            source.source_config['elt.mode.config'][tableName] = tableConfig;
          }
        },
      );
    }

    // Process table filters (for tables without column selections and not full table scan)
    if (config.tableFilters) {
      Object.entries(config.tableFilters).forEach(([tableName, filter]) => {
        if (
          typeof filter === 'string' &&
          filter.trim() &&
          (isAutoMode || config.selection?.[tableName])
        ) {
          if (!source.source_config['elt.mode.config'][tableName]) {
            source.source_config['elt.mode.config'][tableName] = {};
          }
          source.source_config['elt.mode.config'][tableName].filter = Base64.encode(filter.trim())
            // remove the trailing = signs (backend requirement)
            .replace(/=+$/, '');
        }
      });
    }
  }

  if (isAutoMode) {
    source.source_config['cdc.table.config'] = {
      'selection.mode': 'auto',
    };
  } else if (config.autoDetectNewTables && !config.isCDCMode) {
    const excludedTables = allAvailableTables.filter(
      (tableName) => !selectedTables.includes(tableName),
    );

    source.source_config['cdc.table.config'] = {
      'selection.mode': 'semi_auto',
      'selection.criteria': 'exclusion_list',
      'include.tables': selectedTables,
      'exclude.tables': excludedTables,
    };
  } else {
    source.source_config['cdc.table.config'] = {
      'selection.mode': 'manual',
      'selection.criteria': 'inclusion_list',
      'include.tables': selectedTables,
    };
  }

  // add resync tables configuration for non-CDC mode
  if (!config.isCDCMode && config.resyncSelection) {
    const resyncTables = Object.keys(config.resyncSelection).filter(
      (tableName) =>
        config.resyncSelection[tableName] && (isAutoMode || config.selection?.[tableName]),
    );

    if (resyncTables.length > 0) {
      source.source_config['cdc.table.config']['resync.tables'] = resyncTables;
    }
  }

  return source;
};

export const parseSourceConfig = (source, allAvailableTables = []) => {
  const sourceConfig = source?.source_config;
  const config: any = {};
  config.name = source['name'];
  config.description = source['description'];
  config.frequency = sourceConfig?.['start.cron'];
  config.snapshotEnabled = sourceConfig?.['cdc.snapshot.enabled'];
  config.captureDelete = sourceConfig?.['cdc.capture.delete'];

  // Only set captureCreate if it has a value
  const captureCreate = sourceConfig?.['cdc.table.config']?.['capture.create'];
  if (captureCreate !== undefined) {
    config.captureCreate = captureCreate;
  }

  // Only set ui.mode if explicitly present in source_config
  if (sourceConfig?.['ui.mode']) {
    config['ui.mode'] = sourceConfig['ui.mode'];
  }

  // Parse resync tables configuration
  if (sourceConfig?.['cdc.table.config']?.['resync.tables']) {
    const resyncTables = sourceConfig['cdc.table.config']['resync.tables'];
    config.resyncSelection = {};
    resyncTables.forEach((tableName) => {
      config.resyncSelection[tableName] = true;
    });
  } else {
    // Initialize resyncSelection if not present
    config.resyncSelection = {};
  }

  // Parse table filters, column selections, and sync modes from elt.mode.config
  if (sourceConfig?.['elt.mode.config']) {
    config.tableFilters = {};
    config.columnSelections = {};
    config.syncModes = {};

    Object.entries(sourceConfig['elt.mode.config']).forEach(([tableName, tableConfig]) => {
      if (tableConfig && typeof tableConfig === 'object') {
        // Parse sync mode
        if ('mode' in tableConfig) {
          const mode = (tableConfig as any).mode;
          if (mode === 'none') {
            config.syncModes[tableName] = 'full_scan';
          } else if (mode === 'incrementing' || mode === 'timestamp') {
            config.syncModes[tableName] = 'incremental';
          }
        }

        // Parse filter
        if ('filter' in tableConfig) {
          config.tableFilters[tableName] = Base64.decode((tableConfig as any).filter);
        }

        // Parse column selections based on mode
        if ('mode' in tableConfig) {
          const mode = (tableConfig as any).mode;
          if (mode === 'incrementing' && 'incrementing.column.name' in tableConfig) {
            config.columnSelections[tableName] = {
              autoIncrementColumn: (tableConfig as any)['incrementing.column.name'],
            };
          } else if (mode === 'timestamp' && 'timestamp.column.name' in tableConfig) {
            config.columnSelections[tableName] = {
              timestampColumn: (tableConfig as any)['timestamp.column.name'],
            };
          }
        }
      }
    });
  } else {
    // Initialize if not present
    config.tableFilters = {};
    config.columnSelections = {};
    config.syncModes = {};
  }

  config.schemaMode = (() => {
    if (sourceConfig?.['cdc.table.config']?.['selection.mode'] === 'auto') return 'auto';

    return 'name';
  })();

  config.criteriaBehavior = (() => {
    if (sourceConfig?.['cdc.table.config']?.['selection.criteria']?.includes('exclu'))
      return 'exclude';
    return 'include';
  })();

  // Set isCDCMode based on cdc.mode
  config.isCDCMode = sourceConfig?.['cdc.mode'] === 'multi_table';

  // Set autoDetectNewTables based on selection.mode being semi_auto
  config.autoDetectNewTables =
    sourceConfig?.['cdc.table.config']?.['selection.mode'] === 'semi_auto';

  if (sourceConfig?.['cdc.table.config']?.['selection.criteria']?.includes('list')) {
    if (sourceConfig?.['cdc.table.config']?.['selection.criteria']?.includes('exclu')) {
      config.selection = mapTablesToSelection(
        sourceConfig?.['cdc.table.config']?.['exclude.tables'],
      );
    } else {
      config.selection = mapTablesToSelection(
        sourceConfig?.['cdc.table.config']?.['include.tables'],
      );
    }
  }

  // Handle semi_auto mode - use include.tables for selection
  if (sourceConfig?.['cdc.table.config']?.['selection.mode'] === 'semi_auto') {
    // if there are new tables detected which are not yet in include.tables nor
    // exclude.tables, add them to the selection
    const newTables = allAvailableTables.filter(
      (tableName) =>
        !sourceConfig?.['cdc.table.config']?.['include.tables']?.includes(tableName) &&
        !sourceConfig?.['cdc.table.config']?.['exclude.tables']?.includes(tableName),
    );
    config.selection = mapTablesToSelection(
      sourceConfig?.['cdc.table.config']?.['include.tables']?.concat(newTables),
    );
  }
  return config;
};
